/**
 * The country list — score, population, GDP and GDP per capita against
 * Germany's (all four sortable, population and GDP summed in their headers),
 * languages, travel status, and the owner's two
 * decisions per market: exclude it (no market for DUX) or keep it out of the
 * cold campaign (the licensing case stays open). Excluded markets fold below
 * the list and count in no sum. Everything else is the market-access seat's
 * record (`market_upsert`).
 */
import { Fragment, useMemo, useState, type ReactNode } from "react";

import type { EconomyReference, Market } from "../../api/b2g";
import { formatRelative } from "../../lib/formatRelative";
import {
  TRAVEL_LABEL,
  figureTotal,
  formatPopulation,
  formatUsd,
  formatUsdTotal,
  languageName,
  marketStatusTone,
  percent,
  sortMarkets,
  totalScope,
  travelTone,
  type MarketSortKey,
  type SortDir,
} from "../../lib/b2g";
import { Markdown } from "../Markdown";
import { Pill } from "../Pill";

export interface MarketDecision {
  status?: Market["status"];
  campaign_excluded?: boolean;
  reason?: string;
}

interface Sort {
  key: MarketSortKey;
  dir: SortDir;
}

const COLUMNS = 9;

/** A sortable numeric column: the first click sorts largest first, the next one flips it. */
function SortHeader({
  label,
  column,
  sort,
  onSort,
  title,
  children,
}: {
  label: string;
  column: MarketSortKey;
  sort: Sort;
  onSort: (key: MarketSortKey) => void;
  title?: string;
  children?: ReactNode;
}) {
  const active = sort.key === column;
  return (
    <th
      className="num"
      title={title}
      aria-sort={active ? (sort.dir === "desc" ? "descending" : "ascending") : "none"}
    >
      <button type="button" className="th-sort" data-active={active || undefined} onClick={() => onSort(column)}>
        {label}
        <span aria-hidden className="inline-block w-3 text-right">
          {active ? (sort.dir === "desc" ? "↓" : "↑") : ""}
        </span>
      </button>
      {children}
    </th>
  );
}

export function MarketsTable({
  markets,
  germany,
  busyIso,
  onDecide,
}: {
  markets: Market[];
  germany: EconomyReference;
  busyIso: string | null;
  onDecide: (market: Market, decision: MarketDecision) => Promise<void>;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [showExcluded, setShowExcluded] = useState(false);
  const [sort, setSort] = useState<Sort>({ key: "score", dir: "desc" });
  const rows = useMemo(() => sortMarkets(markets, sort.key, sort.dir), [markets, sort]);
  const listed = rows.filter((m) => m.status !== "excluded");
  const excluded = rows.filter((m) => m.status === "excluded");
  const population = figureTotal(markets, (m) => m.population);
  const gdp = figureTotal(markets, (m) => m.gdp_usd);
  const onSort = (key: MarketSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));

  const row = (m: Market) => {
    const expanded = open === m.iso2;
    const statusLabel =
      m.status === "excluded"
        ? "excluded"
        : m.campaign_excluded
          ? `${m.status.replace("_", " ")} · not in campaign`
          : m.status.replace("_", " ");
    return (
      <Fragment key={m.id}>
        <tr
          className="cursor-pointer"
          onClick={() => {
            setOpen(expanded ? null : m.iso2);
            setReason("");
          }}
        >
          <td>
            <div className="font-heading font-semibold text-ink">
              {m.name} <span className="text-xs text-ink-low tnum">{m.iso2}</span>
            </div>
          </td>
          <td>
            <Pill value={statusLabel} tone={marketStatusTone(m)} />
          </td>
          <td className="num tnum">{m.score ?? "–"}</td>
          <td className="num tnum">{m.population !== null ? formatPopulation(m.population) : "–"}</td>
          <td className="num tnum" title={m.economy_year !== null ? `World Bank ${m.economy_year}` : undefined}>
            {m.gdp_usd !== null ? (
              <>
                <div>{formatUsdTotal(m.gdp_usd)}</div>
                <div className="text-xs text-ink-low">{percent(m.gdp_vs_germany)} of Germany</div>
              </>
            ) : (
              "–"
            )}
          </td>
          <td className="num tnum" title={m.economy_year !== null ? `World Bank ${m.economy_year}` : undefined}>
            {m.gdp_per_capita_usd !== null ? (
              <>
                <div>{formatUsd(m.gdp_per_capita_usd)}</div>
                <div className="text-xs text-ink-low">{percent(m.gdp_per_capita_vs_germany)} of Germany</div>
              </>
            ) : (
              "–"
            )}
          </td>
          <td className="text-sm text-ink-medium">
            {m.languages.length ? m.languages.map(languageName).join(", ") : "–"}
          </td>
          <td>
            <Pill value={TRAVEL_LABEL[m.travel_verdict]} tone={travelTone(m)} />
            {m.travel_checked_at ? (
              <div className="text-xs text-ink-low mt-1">
                checked {formatRelative(m.travel_checked_at)}
              </div>
            ) : null}
          </td>
          <td className="text-sm text-ink-low">{m.updated_at ? formatRelative(m.updated_at) : "–"}</td>
        </tr>
        {expanded ? (
          <tr>
            <td colSpan={COLUMNS} className="bg-surface-tint">
              <div className="grid md:grid-cols-2 gap-4 py-2">
                <div>
                  <div className="text-xs text-ink-low mb-1">Score rationale</div>
                  {m.score_rationale ? <Markdown text={m.score_rationale} /> : <p className="text-sm text-ink-low">–</p>}
                  {m.note ? (
                    <>
                      <div className="text-xs text-ink-low mt-3 mb-1">Note</div>
                      <Markdown text={m.note} />
                    </>
                  ) : null}
                  {m.travel_source_url ? (
                    <a className="text-xs link" href={m.travel_source_url} target="_blank" rel="noreferrer">
                      Foreign Office page ↗
                    </a>
                  ) : null}
                </div>
                <div>
                  <div className="text-xs text-ink-low mb-1">Your decision</div>
                  {m.exclusion_reason ? (
                    <p className="text-sm mb-2">{m.exclusion_reason}</p>
                  ) : null}
                  <input
                    className="input w-full mb-2"
                    placeholder="Reason (one line)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex gap-2 flex-wrap">
                    {m.status !== "excluded" ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={busyIso === m.iso2}
                        onClick={() => onDecide(m, { status: "excluded", reason })}
                      >
                        Exclude market
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={busyIso === m.iso2}
                        onClick={() => onDecide(m, { status: "long_list", reason })}
                      >
                        Re-include
                      </button>
                    )}
                    {m.status !== "excluded" ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={busyIso === m.iso2}
                        onClick={() =>
                          onDecide(m, { campaign_excluded: !m.campaign_excluded, reason })
                        }
                      >
                        {m.campaign_excluded ? "Open for the campaign" : "Keep out of the campaign"}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </td>
          </tr>
        ) : null}
      </Fragment>
    );
  };

  return (
    <div className="panel panel--flush overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Market</th>
            <th>Status</th>
            <SortHeader label="Score" column="score" sort={sort} onSort={onSort} />
            <SortHeader
              label="Population"
              column="population"
              sort={sort}
              onSort={onSort}
              title={`Σ = ${totalScope(population)} – World Bank. Germany: ${formatPopulation(germany.population)}`}
            >
              <div className="text-xs font-normal">Σ {formatPopulation(population.total)}</div>
            </SortHeader>
            <SortHeader
              label="GDP"
              column="gdp"
              sort={sort}
              onSort={onSort}
              title={`Population × GDP per capita, World Bank, current US$. Σ = ${totalScope(gdp)}. Germany: ${formatUsdTotal(germany.gdp_usd)} (${germany.economy_year})`}
            >
              <div className="text-xs font-normal">Σ {formatUsdTotal(gdp.total)}</div>
            </SortHeader>
            <SortHeader
              label="GDP per capita"
              column="gdp_per_capita"
              sort={sort}
              onSort={onSort}
              title={`World Bank, current US$ – Germany: ${formatUsd(germany.gdp_per_capita_usd)} (${germany.economy_year})`}
            />
            <th>Languages</th>
            <th>Travel (Auswärtiges Amt)</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {listed.map(row)}
          {excluded.length ? (
            <tr>
              <td colSpan={COLUMNS} className="table-group">
                <button
                  type="button"
                  className="queue-group-toggle"
                  aria-expanded={showExcluded}
                  onClick={() => setShowExcluded((v) => !v)}
                >
                  <span aria-hidden>{showExcluded ? "▾" : "▸"}</span>
                  <span className="font-semibold">Excluded</span>
                  <span className="font-mono text-xs text-ink-low">{excluded.length}</span>
                  <span className="text-xs text-ink-low">– no market for DUX, in no sum</span>
                </button>
              </td>
            </tr>
          ) : null}
          {showExcluded ? excluded.map(row) : null}
        </tbody>
      </table>
    </div>
  );
}
